import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Gift, Plus, Repeat, Users, Coins, Award, Trash2 } from "lucide-react";
import { sr } from "../../lib/sr";

const Head = ({ title, sub }) => (
  <div style={{ marginBottom: 20 }}>
    <h1 style={{ fontSize: 30 }}>{title}</h1>
    {sub && <p className="sr-muted" style={{ marginTop: 6 }}>{sub}</p>}
  </div>
);

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="sr-stat" data-testid={testid}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--muted)", fontSize: 13 }}>
      <Icon size={15} /> {label}
    </div>
    <div style={{ fontSize: 26, fontWeight: 800, color: "var(--navy)", marginTop: 6, fontFamily: "'Cabinet Grotesk',sans-serif" }}>{value}</div>
  </div>
);

export default function LoyaltyLoop() {
  const [summary, setSummary] = useState(null);
  const [program, setProgram] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [members, setMembers] = useState([]);
  const [reward, setReward] = useState({ name: "", points_cost: 100 });
  const [member, setMember] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [savingProg, setSavingProg] = useState(false);

  const loadAll = () => {
    sr.loyaltySummary().then(setSummary).catch(() => setSummary(null));
    sr.loyaltyRewards().then((r) => setRewards(r.rewards)).catch(() => {});
    sr.loyaltyMembers().then((r) => setMembers(r.members)).catch(() => {});
  };
  useEffect(() => {
    sr.loyaltyProgram().then((r) => setProgram(r.program)).catch(() => {});
    loadAll();
  }, []);

  const saveProgram = async () => {
    setSavingProg(true);
    try {
      const r = await sr.updateLoyaltyProgram({
        points_per_visit: Number(program.points_per_visit),
        welcome_bonus: Number(program.welcome_bonus),
        repeat_bonus: Number(program.repeat_bonus),
        point_value_usd: Number(program.point_value_usd),
      });
      setProgram(r.program);
      toast.success("Loyalty program updated");
      loadAll();
    } catch { toast.error("Could not save program"); }
    finally { setSavingProg(false); }
  };

  const addReward = async () => {
    if (!reward.name.trim()) return toast.error("Name your reward");
    setBusy(true);
    try {
      await sr.createReward({ name: reward.name.trim(), points_cost: Number(reward.points_cost) });
      setReward({ name: "", points_cost: 100 });
      sr.loyaltyRewards().then((r) => setRewards(r.rewards));
      toast.success("Reward added");
    } catch { toast.error("Could not add reward"); }
    finally { setBusy(false); }
  };

  const removeReward = async (id) => {
    try { await sr.deleteReward(id); sr.loyaltyRewards().then((r) => setRewards(r.rewards)); }
    catch { toast.error("Could not remove reward"); }
  };

  const enroll = async () => {
    if (!member.email.includes("@")) return toast.error("Enter a valid email");
    setBusy(true);
    try {
      const r = await sr.enrollMember({ name: member.name.trim(), email: member.email.trim().toLowerCase() });
      toast.success(r.created ? "Member enrolled — welcome bonus awarded" : "Member already enrolled");
      setMember({ name: "", email: "" });
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not enroll"); }
    finally { setBusy(false); }
  };

  const visit = async (id) => {
    try { const r = await sr.recordVisit(id); toast.success(`+${r.points_awarded} pts${r.repeat ? " (repeat visit bonus)" : ""}`); loadAll(); }
    catch { toast.error("Could not record visit"); }
  };

  const redeem = async (id, rewardId) => {
    if (!rewardId) return;
    try { const r = await sr.redeemReward(id, rewardId); toast.success(r.note || "Reward redeemed"); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not redeem"); }
  };

  if (!summary || !program) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;

  return (
    <div data-testid="loyalty-page">
      <Head title="Loyalty Loop" sub="Earn → Redeem → Repeat. Turn first-time customers into regulars with RadioLife.app-ready rewards." />

      <div className="sr-card" style={{ background: "var(--blue-light)", border: "1px solid var(--blue)", display: "flex", gap: 12, alignItems: "center" }}>
        <Gift size={22} color="var(--blue)" />
        <p style={{ fontSize: 14 }}>{summary.note}</p>
      </div>

      <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", marginTop: 16 }}>
        <Stat icon={Users} label="Members" value={summary.members} testid="loyalty-stat-members" />
        <Stat icon={Repeat} label="Repeat rate" value={`${summary.repeat_rate_pct}%`} testid="loyalty-stat-repeat" />
        <Stat icon={Coins} label="Points outstanding" value={Number(summary.points_outstanding).toLocaleString()} testid="loyalty-stat-points" />
        <Stat icon={Award} label="Est. liability" value={`$${Number(summary.estimated_liability_usd).toLocaleString()}`} testid="loyalty-stat-liability" />
      </div>

      <div className="sr-grid" style={{ gridTemplateColumns: "1fr 1fr", marginTop: 20 }}>
        {/* Program settings */}
        <div className="sr-card" data-testid="loyalty-program-card">
          <div className="sr-eyebrow" style={{ marginBottom: 12 }}>Earning rules</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="sr-field"><label className="sr-label">Points per visit</label>
              <input className="sr-input" type="number" value={program.points_per_visit}
                onChange={(e) => setProgram({ ...program, points_per_visit: e.target.value })} data-testid="loyalty-ppv" /></div>
            <div className="sr-field"><label className="sr-label">Welcome bonus</label>
              <input className="sr-input" type="number" value={program.welcome_bonus}
                onChange={(e) => setProgram({ ...program, welcome_bonus: e.target.value })} data-testid="loyalty-welcome" /></div>
            <div className="sr-field"><label className="sr-label">Repeat visit bonus</label>
              <input className="sr-input" type="number" value={program.repeat_bonus}
                onChange={(e) => setProgram({ ...program, repeat_bonus: e.target.value })} data-testid="loyalty-repeat-bonus" /></div>
            <div className="sr-field"><label className="sr-label">Point value ($)</label>
              <input className="sr-input" type="number" step="0.01" value={program.point_value_usd}
                onChange={(e) => setProgram({ ...program, point_value_usd: e.target.value })} data-testid="loyalty-point-value" /></div>
          </div>
          <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 12 }} onClick={saveProgram} disabled={savingProg} data-testid="loyalty-save-program">
            {savingProg ? <Loader2 size={15} className="sr-spin" /> : "Save rules"}
          </button>
        </div>

        {/* Rewards */}
        <div className="sr-card" data-testid="loyalty-rewards-card">
          <div className="sr-eyebrow" style={{ marginBottom: 12 }}>Rewards</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input className="sr-input" placeholder="Reward name (e.g. Free appetizer)" value={reward.name}
              onChange={(e) => setReward({ ...reward, name: e.target.value })} data-testid="loyalty-reward-name" />
            <input className="sr-input" type="number" style={{ maxWidth: 110 }} value={reward.points_cost}
              onChange={(e) => setReward({ ...reward, points_cost: e.target.value })} data-testid="loyalty-reward-cost" />
            <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={addReward} disabled={busy} data-testid="loyalty-add-reward"><Plus size={15} /></button>
          </div>
          <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
            {rewards.length === 0 && <p className="sr-muted" style={{ fontSize: 13 }}>No rewards yet. Add one above.</p>}
            {rewards.map((r) => (
              <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 14, padding: "6px 0", borderBottom: "1px solid var(--border)" }} data-testid={`loyalty-reward-${r.id}`}>
                <span><strong style={{ color: "var(--navy)" }}>{r.name}</strong> · {r.points_cost} pts <span className="sr-muted">· {r.redeemed_count || 0} redeemed</span></span>
                <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => removeReward(r.id)} data-testid={`loyalty-del-reward-${r.id}`}><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Members */}
      <div className="sr-card" style={{ marginTop: 20 }} data-testid="loyalty-members-card">
        <div className="sr-eyebrow" style={{ marginBottom: 12 }}>Members</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          <input className="sr-input" placeholder="Name" value={member.name}
            onChange={(e) => setMember({ ...member, name: e.target.value })} data-testid="loyalty-member-name" />
          <input className="sr-input" placeholder="Email" value={member.email}
            onChange={(e) => setMember({ ...member, email: e.target.value })} data-testid="loyalty-member-email" />
          <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={enroll} disabled={busy} data-testid="loyalty-enroll"><Plus size={15} /> Enroll</button>
        </div>
        {members.length === 0 && <p className="sr-muted" style={{ fontSize: 13 }}>No members yet. Enroll a customer or let QR redemptions auto-enroll them.</p>}
        {members.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table className="sr-table" style={{ width: "100%" }}>
              <thead><tr><th>Member</th><th>Tier</th><th>Points</th><th>Visits</th><th>Actions</th></tr></thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} data-testid={`loyalty-member-${m.id}`}>
                    <td><strong style={{ color: "var(--navy)" }}>{m.name}</strong><div className="sr-muted" style={{ fontSize: 12 }}>{m.email}</div></td>
                    <td><span className="sr-badge">{m.tier}</span></td>
                    <td data-testid={`loyalty-balance-${m.id}`}>{m.points_balance}</td>
                    <td>{m.visits}</td>
                    <td>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => visit(m.id)} data-testid={`loyalty-visit-${m.id}`}>+ Visit</button>
                        <select className="sr-select" style={{ maxWidth: 150, padding: "6px 8px" }} defaultValue=""
                          onChange={(e) => { redeem(m.id, e.target.value); e.target.value = ""; }} data-testid={`loyalty-redeem-${m.id}`}>
                          <option value="">Redeem…</option>
                          {rewards.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.points_cost})</option>)}
                        </select>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent activity */}
      <div className="sr-card" style={{ marginTop: 20 }} data-testid="loyalty-activity-card">
        <div className="sr-eyebrow" style={{ marginBottom: 12 }}>Recent loyalty activity</div>
        {(summary.recent_events || []).length === 0 && <p className="sr-muted" style={{ fontSize: 13 }}>Activity appears as members earn and redeem.</p>}
        <div style={{ display: "grid", gap: 6 }}>
          {(summary.recent_events || []).map((e) => (
            <div key={e.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0" }}>
              <span><span className={`sr-badge ${e.type === "REDEEM" ? "sr-badge-gray" : "sr-badge-green"}`} style={{ marginRight: 8 }}>{e.type}</span>{e.reason}</span>
              <strong style={{ color: e.points < 0 ? "var(--danger)" : "var(--success)" }}>{e.points > 0 ? "+" : ""}{e.points} pts</strong>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
